import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  Sheet, 
  SheetContent,
  SheetClose
} from "../ui/sheet";
import { Button } from "../ui/button";
import {
  Home,
  FileText,
  Book,
  BookOpen,
  GraduationCap,
  LineChart,
  MessageSquare,
  Bell,
  User,
  Compass,
  Settings,
  Network,
  HelpCircle,
  Users,
  Flower,
  Brain,
  ChevronDown,
  ChevronRight,
  Briefcase,
  Gamepad2,
  Download
} from "lucide-react";

interface MobileMenuProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function MobileMenu({ isOpen, setIsOpen }: MobileMenuProps) {
  const { t } = useTranslation('mobile');
  const navigate = useNavigate();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  // Lưu thông tin đã đăng nhập hay chưa - trong ví dụ này để đơn giản
  const isLoggedIn = false; 

  // Custom Family icon
  const FamilyIcon = (props) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round" 
      strokeLinejoin="round"
      {...props}
    >
      <circle cx="8" cy="6" r="3" />
      <circle cx="16" cy="6" r="3" />
      <path d="M8 9v12" />
      <path d="M16 9v12" />
      <path d="M12 13v8" />
      <path d="M4 18h8" />
      <path d="M12 18h8" />
    </svg>
  );

  // Menu chính
  const mainNavItems = [
    {
      icon: <Home className="h-5 w-5" />,
      label: t("menu.home"),
      path: "/"
    },
    {
      icon: <Network className="h-5 w-5" />,
      label: t("menu.diagrams"),
      path: "/diagrams"
    },
    {
      icon: <Brain className="h-5 w-5" />,
      label: t("menu.buddhist_studies"),
      path: "/courses/category/phat-hoc"
    },
    {
      icon: <FamilyIcon className="h-5 w-5" />,
      label: t("menu.family_education"),
      path: "/courses/category/giao-duc-gia-dinh"
    },
    {
      icon: <Briefcase className="h-5 w-5" />,
      label: t("menu.work"),
      path: "/courses/category/cong-viec"
    },
    {
      icon: <BookOpen className="h-5 w-5" />,
      label: t("menu.sutra_search"),
      path: "/sutra-search"
    },
    {
      icon: <GraduationCap className="h-5 w-5" />,
      label: t("menu.courses"),
      path: "/courses",
      hasSubmenu: true,
      submenu: [
        {
          icon: <BookOpen className="h-5 w-5" />,
          label: t("menu.all_courses"),
          path: "/courses"
        },
        {
          icon: <Book className="h-5 w-5" />,
          label: t("menu.meditation"),
          path: "/courses/category/thien-dinh",
          layoutType: "meditation"
        },
        {
          icon: <Flower className="h-5 w-5" />,
          label: t("menu.buddhist_philosophy"),
          path: "/courses/category/triet-hoc-phat-giao",
          layoutType: "buddhist-philosophy"
        }
      ]
    },
    {
      icon: <Gamepad2 className="h-5 w-5" />,
      label: t("menu.minigames"),
      path: "/minigames"
    },
    {
      icon: <Download className="h-5 w-5" />,
      label: t("menu.install_app", "Cài đặt ứng dụng"),
      path: "/install-app"
    },
    {
      icon: <FileText className="h-5 w-5" />,
      label: t("menu.about"),
      path: "/about"
    },
    {
      icon: <MessageSquare className="h-5 w-5" />,
      label: t("menu.contact"),
      path: "/contact"
    }
  ];

  // Menu groups
  const menuGroups = [
    {
      title: t("menu.learning"),
      items: [
        {
          icon: <Book className="h-5 w-5" />,
          label: t("menu.my_courses"),
          path: "/my-courses"
        },
        {
          icon: <LineChart className="h-5 w-5" />,
          label: t("menu.my_progress"),
          path: "/my-progress"
        }
      ]
    },
    {
      title: t("menu.notifications"),
      items: [
        {
          icon: <Bell className="h-5 w-5" />,
          label: t("menu.notifications"),
          path: "/notifications"
        }
      ]
    },
    {
      title: t("menu.account"),
      items: [
        {
          icon: <User className="h-5 w-5" />,
          label: t("menu.profile"),
          path: "/profile"
        },
        {
          icon: <Settings className="h-5 w-5" />,
          label: t("menu.settings"),
          path: "/settings"
        }
      ]
    },
    {
      title: t("menu.help"),
      items: [
        {
          icon: <HelpCircle className="h-5 w-5" />,
          label: t("menu.help_center"),
          path: "/help"
        },
        {
          icon: <Users className="h-5 w-5" />,
          label: t("menu.community"),
          path: "/community"
        }
      ]
    }
  ];

  const toggleMenu = (menuTitle: string) => {
    setExpandedMenus(prev => {
      if (prev.includes(menuTitle)) {
        return prev.filter(title => title !== menuTitle);
      } else {
        return [...prev, menuTitle];
      }
    });
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent side="left" className="w-[300px] sm:w-[400px] p-0">
        <nav className="flex flex-col h-full">
          <div className="flex-1 py-4 overflow-y-auto">
            {/* Main navigation items */}
            <div className="px-4 py-2">
              {mainNavItems.map((item, index) => (
                item.hasSubmenu ? (
                  <div key={index}>
                    <Button
                      variant="ghost"
                      className="w-full justify-between"
                      onClick={() => toggleMenu(item.label)}
                    >
                      <div className="flex items-center">
                        {item.icon}
                        <span className="ml-2">{item.label}</span>
                      </div>
                      {expandedMenus.includes(item.label) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                    {expandedMenus.includes(item.label) && item.submenu && (
                      <div className="ml-4">
                        {item.submenu.map((subItem, subIndex) => (
                          <Button
                            key={subIndex}
                            variant="ghost"
                            className="w-full justify-start"
                            onClick={() => handleNavigation(subItem.path)}
                          >
                            {subItem.icon}
                            <span className="ml-2">{subItem.label}</span>
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Button
                    key={index}
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={() => handleNavigation(item.path)}
                  >
                    {item.icon}
                    <span className="ml-2">{item.label}</span>
                  </Button>
                )
              ))}
            </div>

            {/* Menu groups - only shown when logged in */}
            {isLoggedIn && menuGroups.map((group, index) => (
              <div key={index} className="px-4 py-2">
                <h2 className="px-2 mb-2 text-lg font-semibold tracking-tight">
                  {group.title}
                </h2>
                {group.items.map((item, itemIndex) => (
                  <Button
                    key={itemIndex}
                    variant="ghost"
                    className="w-full justify-start"
                    onClick={() => handleNavigation(item.path)}
                  >
                    {item.icon}
                    <span className="ml-2">{item.label}</span>
                  </Button>
                ))}
              </div>
            ))}
          </div>
        </nav>
      </SheetContent>
    </Sheet>
  );
} 